import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toastHelpers } from '@/lib/toast-helpers'
import type { ExpenseTab } from './expensesRouteSearch'

type LedgerFilters = {
  category?: string
  search?: string
  payment_method?: string
}

type Props = {
  /** Which tab is open — the menu offers what that tab is showing. */
  tab: ExpenseTab
  /** ISO bounds from the page's period bar; every export takes the whole window. */
  from: string
  to: string
  /** The P&L workbook is pnlRead-only, so a manager never sees it. */
  canExportPnL: boolean
  /** Granularity of the workbook's Period breakdown sheet. */
  pnlPeriod: string
  /** The ledger's live filters, applied only when the Ledger tab is open. */
  ledgerFilters: LedgerFilters
}

/**
 * One Export button in the page header, beside the period bar — the pattern
 * Reports uses (F-FE-N141). It used to be two buttons buried inside two tabs:
 * a dropdown in the ledger's filter row and a lone "Export P&L (.xlsx)" in the
 * P&L tab's control row, neither of which read as a page action.
 *
 * The menu offers what the open tab is showing, so nothing exports through
 * filters the operator cannot see: the ledger's category / search / method are
 * applied on the Ledger tab and deliberately not on Overview.
 */
export function ExpenseExportMenu({ tab, from, to, canExportPnL, pnlPeriod, ledgerFilters }: Props) {
  const [busy, setBusy] = useState(false)

  const showLedger = tab === 'ledger' || tab === 'overview'
  const showPnL = canExportPnL && (tab === 'pnl' || tab === 'overview')
  const filtered = tab === 'ledger'
  const activeFilterCount = filtered
    ? [ledgerFilters.category, ledgerFilters.search, ledgerFilters.payment_method].filter(Boolean).length
    : 0

  if (!showLedger && !showPnL) return null

  const run = async (fn: () => Promise<void>, ok: string, detail: string) => {
    setBusy(true)
    try {
      await fn()
      toastHelpers.success(ok, detail)
    } catch (err) {
      toastHelpers.apiError('Export', err as Error)
    } finally {
      setBusy(false)
    }
  }

  const ledgerParams = (format: 'xlsx' | 'csv') => ({
    from,
    to,
    format,
    ...(filtered ? ledgerFilters : {}),
  })
  const ledgerDetail = activeFilterCount > 0 ? 'Every expense matching the filters on screen.' : 'Every expense in the selected period.'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-9" disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {showLedger && (
          <>
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Expense ledger
              {activeFilterCount > 0 ? ' · filters applied' : ''}
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={() => run(() => apiClient.exportExpenses(ledgerParams('xlsx')), 'Excel downloaded', ledgerDetail)}>
              Ledger (Excel .xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => run(() => apiClient.exportExpenses(ledgerParams('csv')), 'CSV downloaded', ledgerDetail)}>
              Ledger (CSV)
            </DropdownMenuItem>
          </>
        )}
        {showLedger && showPnL && <DropdownMenuSeparator />}
        {showPnL && (
          <>
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Profit &amp; loss</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() =>
                run(
                  () => apiClient.exportPnLXlsx({ from, to, period: pnlPeriod }),
                  'P&L workbook downloaded',
                  'Statement, period breakdown, expenses by category and a Basis sheet.',
                )
              }
            >
              P&amp;L workbook (Excel .xlsx)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
